import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import BannerOne from "../components/BannerOne";
import FeaturedOne from "../components/FeaturedOne";
import Problems from "../components/Problems";
import ServiceOne from "../components/ServiceOne";
import Faq from "../components/Faq";
import Testimonial from "../components/Testimonial";
import BrandSectionOne from "../components/BrandSectionOne";
import BecomeSellerTwo from "../components/BecomeSellerTwo";
import { useTheme } from "../components/ThemeProvider";
import Preloader from "../helper/Preloader";
import SellingOne from "../components/SellingOne";
import Packages from "../components/Packages";

const HomePage = ({ openModal, isModalOpen, handleOpenModal }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();

  const faqHy = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Ի՞նչ է որակի կառավարման համակարգը (QMS), և ինչ նպատակ ունի։",
        acceptedAnswer: {
          "@type": "Answer",
          text: "QMS-ը գործիք է, որը թույլ է տալիս կազմակերպություններին սահմանել սպասարկման ստանդարտներ, վերահսկել դրանց պահպանումը և բարելավել գործընթացները՝ տվյալների հիման վրա։",
        },
      },
      {
        "@type": "Question",
        name: "Ինչու՞ է իմ կազմակերպությանը անհրաժեշտ նման համակարգ։",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Եթե ունեք հաճախորդների սպասարկում, սպասարկման թիմ կամ սպասման հերթեր, ապա QMS-ը կօգնի դարձնել ամեն ինչ կառավարվող, կանխատեսելի և տվյալահեն՝ կրճատելով սպասման ժամանակը և բարձրացնելով գոհունակությունը։",
        },
      },
      {
        "@type": "Question",
        name: "Կարո՞ղ եմ տեսնել բոլոր մասնաճյուղերի վիճակը մեկ կենտրոնից։",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Այո։ Նոմիո-ն տրամադրում է կենտրոնացված դեշբորդ, որտեղ տեսանելի են բոլոր մասնաճյուղերի կատարողականը, հաճախորդների հոսքը և աշխատակիցների ծանրաբեռնվածությունը։",
        },
      },
      {
        "@type": "Question",
        name: "Արդյոք հնարավոր է հաճախորդները նախապես ամրագրեն հերթը։",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Այո։ Հերթերը հնարավոր է ամրագրել վեբ պորտալով, բջջային հավելվածով կամ տեղում՝ տերմինալի միջոցով։ Ամրագրման կարգավիճակը կարելի է վերահսկել իրական ժամանակում։",
        },
      },
      {
        "@type": "Question",
        name: "Արդյո՞ք անհրաժեշտ է տեխնիկական թիմ՝ Նոմիո-ն ներդնելու համար։",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ոչ։ Համակարգը ունի պարզ ինտերֆեյս, ուսուցման նյութեր և 24/7 աջակցություն։ Բացի այդ, ներդրման գործընթացը ուղեկցվում է մեր մասնագետների կողմից։",
        },
      },
    ],
  };

  const faqEn = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a Quality Management System (QMS) and what is its purpose?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A QMS is a tool that allows organizations to define service standards, monitor their implementation, and improve processes based on data.",
        },
      },
      {
        "@type": "Question",
        name: "Why does my organization need a QMS?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If you have customer service, support teams, or waiting queues, a QMS helps you make everything more manageable, predictable, and data-driven—reducing wait times and improving satisfaction.",
        },
      },
      {
        "@type": "Question",
        name: "Can I monitor all branches from a central dashboard?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Nomio provides a centralized dashboard where you can track performance, customer flow, and staff workload across all branches.",
        },
      },
      {
        "@type": "Question",
        name: "Can customers book a queue in advance?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Queues can be booked through the web portal, mobile app, or on-site terminal. Booking statuses can be tracked in real time.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need a technical team to implement Nomio?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. The system has a simple interface, training materials, and 24/7 support. Plus, implementation is handled by our expert team.",
        },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(i18n.language === "hy" ? faqHy : faqEn)}
        </script>
      </Helmet>

      <section className="change-gradient">
        {/* Preloader */}
        <Preloader />

        <BannerOne
          setOpenGetDemoModal={() => openModal("getDemo")}
          setOpenCalendlyModal={() => openModal("calendly")}
        />

        <BrandSectionOne />

        <div id="about">
          <FeaturedOne
            title={t("aboutSection.title")}
            descriptionLine1={t("aboutSection.desc")}
            direction="text-left"
            img={`/assets/images/thumbs/aboutUs_img_${isDarkMode ? "light" : "dark"}.webp`}
            withoutPadding
            isGradient
          />
        </div>

        <FeaturedOne
          title={t("aboutApp.title")}
          descriptionLine1={t("aboutApp.desc1")}
          descriptionLine2={t("aboutApp.desc2")}
          direction="text-right"
          showBtn
          btn1Text={t("aboutApp.buttons.ios")}
          btn2Text={t("aboutApp.buttons.android")}
          img="/assets/images/thumbs/order-mobile-app.webp"
          isContainer
        />

        <FeaturedOne
          title={t("aboutWeb.title")}
          descriptionLine1={t("aboutWeb.desc1")}
          descriptionLine2={t("aboutWeb.desc2")}
          direction="text-left"
          img="/assets/images/thumbs/order-web-app.webp"
          isContainer
        />

        {/* <Packages openModal={openModal} /> */}

        <Problems direction="text-right" />

        <ServiceOne />

        <SellingOne
          isModalOpen={isModalOpen}
          handleOpenModal={handleOpenModal}
        />

        <Testimonial />

        <BecomeSellerTwo setOpenEbookModal={() => openModal("ebook")} />

        <Faq />
      </section>
    </>
  );
};

export default HomePage;
